import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavbarAdmin from "../components/NavbarAdmin";
import API from "../config";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import {
  AreaChart, Area,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, Cell,
  PieChart, Pie,
} from "recharts";

/* ─── Constants ─── */
const BAR_COLORS = ["#ef4444","#f97316","#eab308","#22c55e","#3b82f6","#8b5cf6","#ec4899"];
const DONUT_COLORS = ["#3b82f6","#8b5cf6","#22c55e","#f97316","#ec4899","#06b6d4"];
const ACTIVE     = ["Approved","Confirmed","CheckedIn","CheckedOut"];

const STATUS_LABEL_KEY = {
  Pending:"dbStatusPending", Approved:"dbStatusApproved", Confirmed:"dbStatusConfirmed",
  CheckedIn:"dbStatusCheckedIn", CheckedOut:"dbStatusCheckedOut", Cancelled:"dbStatusCancelled",
};
const STATUS_COLOR = {
  Pending:"#f97316", Approved:"#22c55e", Confirmed:"#3b82f6",
  CheckedIn:"#8b5cf6", CheckedOut:"#94a3b8", Cancelled:"#ef4444",
};
const STATUS_BG = {
  Pending:"#fff7ed", Approved:"#f0fdf4", Confirmed:"#eff6ff",
  CheckedIn:"#f5f3ff", CheckedOut:"#f8fafc", Cancelled:"#fef2f2",
};

/* ─── Inline styles ─── */
const S = {
  page: {
    minHeight: "100vh",
    background: "#f8fafc",
    fontFamily: "'Noto Sans Thai',sans-serif",
    color: "#1e293b",
  },
  body: {
    padding: "24px 28px 40px",
    maxWidth: "1600px",
    margin: "0 auto",
  },
  headerRow: {
    display: "flex", alignItems: "flex-start", gap: 16,
    marginBottom: 20, flexWrap: "wrap",
  },
  titleBlock: {
    display: "flex", alignItems: "center", gap: 10, minWidth: 160,
  },
  titleIconWrap: {
    width: 40, height: 40, borderRadius: 10,
    background: "#eff6ff", display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  titleIcon: { fontSize: 20, color: "#3b82f6" },
  title: { fontSize: 22, fontWeight: 700, color: "#0f172a", margin: 0, whiteSpace: "nowrap" },
  statsRow: { display: "flex", gap: 10, flex: 1, flexWrap: "wrap" },
  statCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: "14px 18px",
    flex: 1,
    minWidth: 140,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  statLabel: { fontSize: 11, color: "#94a3b8", marginBottom: 4, whiteSpace: "nowrap", letterSpacing: .5, fontWeight: 600, textTransform: "uppercase" },
  statValue: { fontSize: 20, fontWeight: 700, color: "#0f172a" },
  statChange: { fontSize: 11, color: "#22c55e", fontWeight: 600, marginTop: 2, display: "flex", alignItems: "center", gap: 2 },
  mainGrid: { display: "grid", gridTemplateColumns: "1fr 240px", gap: 16, alignItems: "start" },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: "18px 20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  cardTitle: { fontSize: 12, fontWeight: 700, color: "#94a3b8", letterSpacing: .5, textTransform: "uppercase" },
  botRow: { display: "grid", gridTemplateColumns: "1.1fr 1fr 1.15fr", gap: 14, marginTop: 16 },
  skel: { background: "#f1f5f9", borderRadius: 6, animation: "pulse 1.4s ease-in-out infinite" },
  notifItem: {
    display: "flex", gap: 10, alignItems: "flex-start",
    paddingBottom: 12, borderBottom: "1px solid #f1f5f9",
  },
  notifIcon: {
    fontSize: 12, background: "#eff6ff", width: 28, height: 28,
    borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, color: "#3b82f6", fontWeight: 700,
  },
  notifTxt:  { fontSize: 12, color: "#475569", lineHeight: 1.5 },
  notifTime: { fontSize: 11, color: "#94a3b8", marginTop: 2 },
  revItem:  { display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 },
  revImg:   { width: 52, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0, background: "#f1f5f9" },
  revRoom:  { fontSize: 11, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  revGuest: { fontSize: 10, color: "#94a3b8", marginBottom: 3 },
  revStatus:{ fontSize: 11 },
  legRow: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#94a3b8" },
  legDot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
  donutWrap:   { display: "flex", alignItems: "center", gap: 10 },
  donutLegend: { display: "flex", flexDirection: "column", gap: 5 },
  donutItem:   { display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#94a3b8" },
  donutDot:    { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  donutPct:    { fontWeight: 700, color: "#0f172a", minWidth: 34, textAlign: "right" },
  emptyTxt:    { fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "20px 0" },
};

/* ─── Toggle Pill Component ─── */
function ViewToggle({ value, onChange, options, labels }) {
  return (
    <div style={{
      display: "flex", gap: 2,
      background: "#f1f5f9",
      borderRadius: 8, padding: 2,
      flexShrink: 0,
    }}>
      {options.map((opt, idx) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          style={{
            fontSize: 10, fontWeight: 600, padding: "3px 8px",
            borderRadius: 6, border: "none", cursor: "pointer",
            fontFamily: "inherit",
            background: value === opt ? "#fff" : "transparent",
            color: value === opt ? "#0f172a" : "#94a3b8",
            boxShadow: value === opt ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
            transition: "all .15s",
            lineHeight: 1.6,
          }}
        >
          {labels ? labels[idx] : opt}
        </button>
      ))}
    </div>
  );
}

/* ─── Sub-components ─── */
function Skeleton({ h = 24, w = "100%", r = 6 }) {
  return <div style={{ ...S.skel, height: h, width: w, borderRadius: r }} />;
}

function StatCard({ label, value, change, isDown, loading }) {
  const changeColor = isDown ? "#ef4444" : "#22c55e";
  return (
    <div className="db-stat-card" style={S.statCard}>
      <div className="db-stat-label" style={S.statLabel}>{label}</div>
      {loading
        ? <Skeleton h={22} w="75%" />
        : <>
            <div className="db-stat-value" style={S.statValue}>{value}</div>
            {change && <div style={{ ...S.statChange, color: changeColor }}>{change}</div>}
          </>
      }
    </div>
  );
}

/* ── Tooltip ── */
function makeAreaTooltip(lang) {
  return function CustomAreaTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
      <div style={{
        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
        padding: "8px 14px", fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}>
        <div style={{ color: "#94a3b8", fontWeight: 700, marginBottom: 4, fontSize: 11 }}>{label}</div>
        {[...payload].reverse().map((p, i) => (
          <div key={i} style={{ color: p.name === "thisYear" ? "#06b6d4" : "#94a3b8", marginBottom: 2 }}>
            {p.name === "thisYear" ? t(lang, "dbThisYear") : t(lang, "dbLastYear")}: ฿{Number(p.value).toLocaleString()}
          </div>
        ))}
      </div>
    );
  };
}

/* ─── Main Component ─── */
export default function AdminDashboard() {
  const navigate  = useNavigate();
  const { lang }  = useLanguage();
  const MONTHS    = t(lang, "dbMonths");
  const DAYS      = t(lang, "dbDays");
  const DAYS_FULL = t(lang, "dbDaysFull");
  const token     = localStorage.getItem("token");
  const NOW       = new Date();
  const thisYear  = NOW.getFullYear();
  const thisMonth = NOW.getMonth() + 1;

  const [loaded,    setLoaded]    = useState(false);
  const [fetching,  setFetching]  = useState(true);
  const [bookings,  setBookings]  = useState([]);
  const [payments,  setPayments]  = useState([]);
  const [rooms,     setRooms]     = useState([]);
  const [units,     setUnits]     = useState([]);

  /* ── Toggle states ── */
  const [barView,   setBarView]   = useState("monthly");
  const [donutView, setDonutView] = useState("monthly");

  const fetchAll = useCallback(async () => {
    setFetching(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      const opts = { credentials: "include", headers: h };
      const [bR, pR, rR, uR] = await Promise.all([
        fetch(`${API}/api/bookings`,       opts),
        fetch(`${API}/api/admin/payments`, opts),
        fetch(`${API}/api/rooms`,          opts),
        fetch(`${API}/api/room-units`,     opts),
      ]);
      const [b, p, r, u] = await Promise.all([bR.json(), pR.json(), rR.json(), uR.json()]);
      setBookings(Array.isArray(b) ? b : []);
      setPayments(Array.isArray(p) ? p : []);
      setRooms(Array.isArray(r) ? r : []);
      setUnits(Array.isArray(u) ? u : []);
    } catch (e) { console.error(e); }
    finally {
      setFetching(false);
      setTimeout(() => setLoaded(true), 80);
    }
  }, [token]);

  useEffect(() => {
    if (localStorage.getItem("role") !== "admin") { navigate("/login"); return; }
    fetchAll();
  }, [navigate, fetchAll]);

  /* ── Derived data ── */
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);

  const checkedOut = bookings.filter(b => b.status === "CheckedOut");
  // ใช้เวลาเช็คเอาท์จริง (checkedOutAt) เป็นหลักในการรับรู้รายได้ — checkOut คือแค่วันที่จองไว้ล่วงหน้า
  const revDate = b => new Date(b.checkedOutAt || b.checkOut);

  const revenueData = MONTHS.map((m, i) => {
    const mon = i + 1;
    const sum = yr => checkedOut
      .filter(b => revDate(b).getFullYear() === yr && revDate(b).getMonth() + 1 === mon)
      .reduce((s, b) => s + (b.totalAmount || 0) + (b.extraTotal || 0), 0);
    return { month: m, thisYear: sum(thisYear), lastYear: sum(thisYear - 1) };
  });

  const revenueYear      = revenueData.reduce((s, d) => s + d.thisYear, 0);
  const revenueYearLast  = revenueData.reduce((s, d) => s + d.lastYear, 0);
  const revenueMonth     = revenueData[thisMonth - 1]?.thisYear || 0;
  const revenueMonthLast = (() => {
    const prevMon = thisMonth === 1 ? 12 : thisMonth - 1;
    const prevYr  = thisMonth === 1 ? thisYear - 1 : thisYear;
    return checkedOut
      .filter(b => revDate(b).getFullYear() === prevYr && revDate(b).getMonth() + 1 === prevMon)
      .reduce((s, b) => s + (b.totalAmount || 0) + (b.extraTotal || 0), 0);
  })();
  const revenueDay = checkedOut
    .filter(b => { const d = revDate(b); d.setHours(0,0,0,0); return d.getTime() === today.getTime(); })
    .reduce((s, b) => s + (b.totalAmount || 0) + (b.extraTotal || 0), 0);
  const revenueDayYest = checkedOut
    .filter(b => { const d = revDate(b); d.setHours(0,0,0,0); return d.getTime() === yesterday.getTime(); })
    .reduce((s, b) => s + (b.totalAmount || 0) + (b.extraTotal || 0), 0);

  const pctChange = (cur, prev) => {
    if (prev === 0) return cur > 0 ? "+100%" : "0%";
    const p = ((cur - prev) / prev * 100).toFixed(1);
    return (p >= 0 ? "+" : "") + p + "%";
  };
  const changeDir = (cur, prev) => cur >= prev ? "↗" : "↘";

  const totalGuests = new Set(
    checkedOut.filter(b => b.user).map(b => (b.user?._id ?? b.user)?.toString())
  ).size;
  const totalGuestsLastYear = new Set(
    bookings.filter(b => b.status === "CheckedOut" && new Date(b.checkOut).getFullYear() === thisYear - 1 && b.user)
      .map(b => (b.user?._id ?? b.user)?.toString())
  ).size;

  /* ── Bar chart — รายเดือน (แยกตามวันในสัปดาห์) ── */
  const guestByDay = Array(7).fill(0);
  bookings.forEach(b => {
    const d = new Date(b.checkIn);
    if (d.getFullYear() === thisYear && d.getMonth() + 1 === thisMonth) guestByDay[d.getDay()]++;
  });
  const guestData = DAYS.map((day, i) => ({ day, dayFull: DAYS_FULL[i], count: guestByDay[i], color: BAR_COLORS[i] }));

  /* ── Bar chart — รายสัปดาห์ (7 วันย้อนหลัง) ── */
  const guestDataWeek = (() => {
    const result = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const label = `${d.getDate()}/${d.getMonth() + 1}`;
      const count = bookings.filter(b => {
        const bd = new Date(b.checkIn); bd.setHours(0, 0, 0, 0);
        return bd.getTime() === d.getTime();
      }).length;
      result.push({ day: label, count, color: BAR_COLORS[(6 - i) % BAR_COLORS.length] });
    }
    return result;
  })();

  /* ── Donut — รายเดือน (ทั้งเดือนปัจจุบัน = 4 สัปดาห์รวม) ── */
  const monthStart = new Date(thisYear, thisMonth - 1, 1);
  const monthEnd   = new Date(thisYear, thisMonth, 0, 23, 59, 59, 999);
  const rtCount = {};
  bookings.forEach(b => {
    const d = new Date(b.checkIn);
    if (d >= monthStart && d <= monthEnd) {
      const n = b.roomType?.name || t(lang, "dbOther");
      rtCount[n] = (rtCount[n] || 0) + 1;
    }
  });
  const totalBk = Object.values(rtCount).reduce((s, c) => s + c, 0) || 1;
  const roomUsageData = Object.entries(rtCount)
    .map(([name, c], i) => ({ name, value: Math.round(c / totalBk * 1000) / 10, color: DONUT_COLORS[i % DONUT_COLORS.length] }))
    .sort((a, b) => b.value - a.value).slice(0, 5);

  /* ── Donut — รายสัปดาห์ (จันทร์–อาทิตย์ของสัปดาห์ปัจจุบัน) ── */
  const currentWeekStart = new Date(today);
  const dayOfWeek = today.getDay();
  const diffToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  currentWeekStart.setDate(today.getDate() + diffToMon);
  currentWeekStart.setHours(0, 0, 0, 0);
  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
  currentWeekEnd.setHours(23, 59, 59, 999);
  const rtCountWeek = {};
  bookings.forEach(b => {
    const d = new Date(b.checkIn);
    if (d >= currentWeekStart && d <= currentWeekEnd) {
      const n = b.roomType?.name || t(lang, "dbOther");
      rtCountWeek[n] = (rtCountWeek[n] || 0) + 1;
    }
  });
  const totalBkWeek = Object.values(rtCountWeek).reduce((s, c) => s + c, 0) || 1;
  const roomUsageDataWeek = Object.entries(rtCountWeek)
    .map(([name, c], i) => ({ name, value: Math.round(c / totalBkWeek * 1000) / 10, color: DONUT_COLORS[i % DONUT_COLORS.length] }))
    .sort((a, b) => b.value - a.value).slice(0, 5);

  /* ── active data based on toggle ── */
  const activeBarData   = barView   === "monthly" ? guestData        : guestDataWeek;
  const activeDonutData = donutView === "monthly" ? roomUsageData     : roomUsageDataWeek;

  const CustomAreaTooltip = makeAreaTooltip(lang);

  /* notifications */
  const notifs = [];
  const timeAgo = (diff) => diff < 60 ? `${diff} ${t(lang,"dbMinAgo")}` : diff < 1440 ? `${Math.floor(diff / 60)} ${t(lang,"dbHrsAgo")}` : t(lang,"dbYesterday");
  [...bookings].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 3).forEach(b => {
    const diff = Math.floor((Date.now() - new Date(b.createdAt)) / 60000);
    notifs.push({ icon: "→", text: `${t(lang,"dbNewBooking")} ${b.roomType?.name || ""}`, time: timeAgo(diff), color: "#3b82f6", bg: "#eff6ff" });
  });
  [...payments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 2).forEach(p => {
    const diff = Math.floor((Date.now() - new Date(p.createdAt)) / 60000);
    notifs.push({ icon: "฿", text: `${t(lang,"dbPayment")} ฿${Number(p.amount || 0).toLocaleString()}`, time: timeAgo(diff), color: "#22c55e", bg: "#f0fdf4" });
  });

  /* recent */
  const recentBk = [...bookings].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 3);

  /* ── Render ── */
  return (
    <>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:none} }
        .db-loaded { animation: fadeUp .4s ease both; }
        @media(max-width:1100px) { .db-main-grid{grid-template-columns:1fr!important} }
        @media(max-width:780px)  { .db-bot-row{grid-template-columns:1fr!important} .db-stats-row{gap:8px!important} }
        @media(max-width:600px)  {
          .db-title-block { flex:1 1 auto!important; order:1!important; min-width:0!important; }
          .db-refresh-btn { order:2!important; align-self:center!important; }
          .db-stats-row { order:3!important; width:100%!important; flex-basis:100%!important; display:grid!important; grid-template-columns:1fr 1fr!important; flex:unset!important; }
          .db-stat-card { min-width:0!important; padding:12px 14px!important; }
          .db-stat-value { font-size:17px!important; }
          .db-stat-label { font-size:10px!important; white-space:normal!important; }
        }
        ::-webkit-scrollbar { width:4px; height:4px }
        ::-webkit-scrollbar-track { background:transparent }
        ::-webkit-scrollbar-thumb { background:#e2e8f0; border-radius:4px }
        .db-refresh-btn:hover { border-color:#06b6d4!important; color:#06b6d4!important; background:#ecfeff!important; }
      `}</style>

      <div style={S.page}>
        <NavbarAdmin />

        <div className={loaded ? "db-loaded" : ""} style={S.body}>

          {/* ── Header ── */}
          <div className="db-header-row" style={S.headerRow}>
            <div className="db-title-block" style={S.titleBlock}>
              <div style={S.titleIconWrap}>
                <span style={S.titleIcon}>⊞</span>
              </div>
              <h2 style={S.title}>{t(lang, "dbDashboard")}</h2>
            </div>

            <div className="db-stats-row" style={S.statsRow}>
              <StatCard label={t(lang, "dbRevenueYear")}   value={`฿${revenueYear.toLocaleString()}`}  change={`${pctChange(revenueYear, revenueYearLast)} ${changeDir(revenueYear, revenueYearLast)}`}   isDown={revenueYear < revenueYearLast}     loading={fetching} />
              <StatCard label={t(lang, "dbRevenueMonth")}  value={`฿${revenueMonth.toLocaleString()}`} change={`${pctChange(revenueMonth, revenueMonthLast)} ${changeDir(revenueMonth, revenueMonthLast)}`} isDown={revenueMonth < revenueMonthLast}   loading={fetching} />
              <StatCard label={t(lang, "dbRevenueDay")}    value={`฿${revenueDay.toLocaleString()}`}   change={`${pctChange(revenueDay, revenueDayYest)} ${changeDir(revenueDay, revenueDayYest)}`}         isDown={revenueDay < revenueDayYest}       loading={fetching} />
              <StatCard label={t(lang, "dbTotalGuests")}   value={totalGuests.toLocaleString()}         change={`${pctChange(totalGuests, totalGuestsLastYear)} ${changeDir(totalGuests, totalGuestsLastYear)}`} isDown={totalGuests < totalGuestsLastYear} loading={fetching} />
            </div>

            <button
              onClick={fetchAll}
              disabled={fetching}
              className="db-refresh-btn"
              style={{
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
                color: "#94a3b8", padding: "8px 14px", fontSize: 12, cursor: "pointer",
                flexShrink: 0, transition: "all .2s", fontFamily: "inherit",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              {fetching ? "..." : t(lang, "dbRefresh")}
            </button>
          </div>

          {/* ── Main grid ── */}
          <div className="db-main-grid" style={S.mainGrid}>

            {/* ── Charts column ── */}
            <div style={{ minWidth: 0 }}>

              {/* ── Area chart ── */}
              <div style={{ ...S.card, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div>
                    <span style={S.cardTitle}>{t(lang, "dbMonthlyRevenue")}</span>
                    <div style={{ fontSize: 10, color: "#cbd5e1", letterSpacing: .3 }}>{t(lang,"dbRevenueAccYear")}</div>
                  </div>
                  <div style={S.legRow}>
                    <span style={{ ...S.legDot, background: "#06b6d4" }} />
                    <span>{t(lang, "dbThisYear")}</span>
                    <span style={{
                      display: "inline-block", width: 18, height: 0,
                      borderTop: "2px dashed #94a3b8", marginLeft: 14, marginRight: 4,
                      verticalAlign: "middle",
                    }} />
                    <span>{t(lang, "dbLastYear")}</span>
                  </div>
                </div>

                {fetching
                  ? <Skeleton h={220} />
                  : <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={revenueData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradThisYear" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%"   stopColor="#06b6d4" stopOpacity={0.18} />
                            <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false}
                          tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                        <Tooltip content={<CustomAreaTooltip />} />
                        <Area type="monotone" dataKey="lastYear" name="lastYear"
                          stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 4"
                          fill="none" dot={false}
                          activeDot={{ r: 3, fill: "#94a3b8", strokeWidth: 0 }} />
                        <Area type="monotone" dataKey="thisYear" name="thisYear"
                          stroke="#06b6d4" strokeWidth={2.5}
                          fill="url(#gradThisYear)" dot={false}
                          activeDot={{ r: 4, fill: "#06b6d4", strokeWidth: 0 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                }
              </div>

              {/* ── Bottom row ── */}
              <div className="db-bot-row" style={S.botRow}>

                {/* ── Bar chart ── */}
                <div style={S.card}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={S.cardTitle}>{t(lang, "dbCheckins")}</div>
                    <ViewToggle
                      value={barView}
                      onChange={setBarView}
                      options={["monthly", "weekly"]}
                      labels={[t(lang, "dbMonthly"), t(lang, "dbWeekly")]}
                    />
                  </div>
                  {fetching
                    ? <Skeleton h={140} />
                    : <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={activeBarData} barSize={16} margin={{ top: 0, right: 0, left: -24, bottom: 0 }}>
                          <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                          <YAxis hide />
                          <Tooltip
                            contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0].payload;
                              return (
                                <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:8, padding:"8px 12px", fontSize:12, boxShadow:"0 4px 12px rgba(0,0,0,0.08)" }}>
                                  <div style={{ fontWeight:600, color:"#374151", marginBottom:2 }}>{d.dayFull || d.day}</div>
                                  <div style={{ color:"#6b7280" }}>{t(lang,"dbBookings")} : {d.count}</div>
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                            {activeBarData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                  }
                </div>

                {/* ── Donut ── */}
                <div style={S.card}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <div style={S.cardTitle}>{t(lang, "dbRoomUsage")}</div>
                    <ViewToggle
                      value={donutView}
                      onChange={setDonutView}
                      options={["monthly", "weekly"]}
                      labels={[t(lang, "dbMonthly"), t(lang, "dbWeekly")]}
                    />
                  </div>
                  {fetching
                    ? <Skeleton h={120} />
                    : activeDonutData.length === 0
                      ? <div style={S.emptyTxt}>{t(lang, "dbNoGuests")}</div>
                      : <div style={S.donutWrap}>
                          <PieChart width={112} height={112}>
                            <Pie
                              data={activeDonutData} cx={52} cy={52}
                              innerRadius={32} outerRadius={50}
                              dataKey="value" paddingAngle={2}
                            >
                              {activeDonutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                            </Pie>
                          </PieChart>
                          <div style={S.donutLegend}>
                            {activeDonutData.map((d, i) => (
                              <div key={i} style={S.donutItem}>
                                <span style={{ ...S.donutDot, background: d.color }} />
                                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                                <span style={S.donutPct}>{d.value}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                  }
                </div>

                {/* ── Recent bookings ── */}
                <div style={S.card}>
                  <div style={{ ...S.cardTitle, marginBottom: 12 }}>{t(lang, "dbRecentBookings")}</div>
                  {fetching
                    ? [1, 2, 3].map(i => <div key={i} style={{ ...S.skel, height: 48, borderRadius: 8, marginBottom: 10 }} />)
                    : recentBk.length === 0
                      ? <div style={S.emptyTxt}>{t(lang, "dbNoBookings")}</div>
                      : recentBk.map((b, i) => {
                          const guest = b.user
                            ? `${b.user.firstname || ""} ${b.user.lastname || ""}`.trim() || b.user.email || b.guestName || "—"
                            : b.guestName || "—";
                          const img = b.roomType?.image
                            ? `/uploads/${b.roomType.image}`
                            : "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=80&h=60&fit=crop";
                          return (
                            <div key={i} style={S.revItem}>
                              <img src={img} alt="" style={S.revImg} onError={e => e.target.style.display = "none"} />
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={S.revRoom}>{b.roomType?.name || "—"} : {b.roomUnit?.roomNumber || "—"}</div>
                                <div style={S.revGuest}>{guest}</div>
                                <div style={{ ...S.revStatus, color: STATUS_COLOR[b.status] || "#94a3b8" }}>
                                  <span style={{
                                    background: STATUS_BG[b.status] || "#f8fafc",
                                    color: STATUS_COLOR[b.status] || "#94a3b8",
                                    fontSize: 10, fontWeight: 700, padding: "2px 7px",
                                    borderRadius: 20, display: "inline-block",
                                  }}>
                                    {STATUS_LABEL_KEY[b.status] ? t(lang, STATUS_LABEL_KEY[b.status]) : b.status}
                                  </span>
                                  <span style={{ marginLeft: 6, fontSize: 11, color: "#94a3b8" }}>฿{Number(b.totalAmount || 0).toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                  }
                </div>

              </div>
            </div>

            {/* ── Notifications column ── */}
            <div style={{ ...S.card, position: "sticky", top: 80 }}>
              <div style={{ ...S.cardTitle, marginBottom: 16 }}>{t(lang, "dbNotifications")}</div>
              {fetching
                ? [1, 2, 3, 4].map(i => <div key={i} style={{ ...S.skel, height: 44, borderRadius: 8, marginBottom: 10 }} />)
                : notifs.length === 0
                  ? <div style={S.emptyTxt}>{t(lang, "dbNoNotifs")}</div>
                  : notifs.map((n, i) => (
                      <div key={i} style={{ ...S.notifItem, ...(i === notifs.length - 1 ? { borderBottom: "none", paddingBottom: 0 } : {}) }}>
                        <div style={{ ...S.notifIcon, background: n.bg || "#eff6ff", color: n.color || "#3b82f6" }}>{n.icon}</div>
                        <div>
                          <div style={S.notifTxt}>{n.text}</div>
                          <div style={S.notifTime}>{n.time}</div>
                        </div>
                      </div>
                    ))
              }

              {!fetching && bookings.length > 0 && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
                  <div style={{ ...S.cardTitle, marginBottom: 10 }}>{t(lang, "dbBookingStatus")}</div>
                  {Object.entries(STATUS_LABEL_KEY).map(([key, labelKey]) => {
                    const cnt = bookings.filter(b => b.status === key).length;
                    if (!cnt) return null;
                    return (
                      <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: "#64748b" }}>{t(lang, labelKey)}</span>
                        <span style={{
                          fontSize: 11, fontWeight: 700,
                          background: STATUS_BG[key] || "#f8fafc",
                          color: STATUS_COLOR[key] || "#64748b",
                          padding: "2px 8px", borderRadius: 20,
                        }}>{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}